import {
  ServerError,
  BadRequestError,
  UnauthenticatedError,
  FormSubmissionError,
  PageNotFoundError
} from './errors';
import HTTPMethods from './http-methods';
import { getAuthHandler } from './auth-handler';
import { resolveRequestInfo } from './path-resolver';

const STOCK_HEADERS: HeadersInit = {
  'Content-Type': 'application/json'
};

/**
 * Flatten HeadersInit so it can be object-spread. A Headers instance has no
 * enumerable own properties, so `{ ...headers }` drops every entry — including
 * `Accept-Cache` from the worklist two-phase case-detail fetch.
 */
const headersInitToRecord = (
  headers?: HeadersInit
): Record<string, string> => {
  if (!headers) {
    return {};
  }
  if (headers instanceof Headers) {
    const record: Record<string, string> = {};
    headers.forEach((value, key) => {
      record[key] = value;
    });
    return record;
  }
  if (Array.isArray(headers)) {
    return Object.fromEntries(headers);
  }
  return { ...headers };
};

const isX = (startInclusive: number, endExclusive: number) => {
  return (statusCode: number) =>
    statusCode >= startInclusive && statusCode < endExclusive;
};

const is500 = isX(500, 600);

const _execRequest = async (
  token: string,
  requestInfo: RequestInfo,
  requestInit: RequestInit = {}
): Promise<Response> => {
  const authHeaders = token
    ? {
        Authorization: token
      }
    : {};
  const headers: HeadersInit = {
    ...STOCK_HEADERS,
    ...authHeaders,
    ...headersInitToRecord(requestInit.headers)
  };
  const options: RequestInit = {
    method: HTTPMethods.GET,
    cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
    redirect: 'follow',
    referrer: 'no-referrer',
    ...requestInit,
    headers
  };
  if (requestInit.body) {
    options.body = requestInit.body;
  }

  const response = await fetch(resolveRequestInfo(requestInfo), options);
  if (!response.ok) {
    const { status } = response;
    if (is500(status)) {
      throw new ServerError(`error contacting api ${requestInfo}`);
    } else if (status === 400) {
      const responseBody = await response.json();
      const error = new BadRequestError(`error contacting api`, response, responseBody);
      // Attach the parsed response data to the error object for easy access
      (error as any).responseData = responseBody;
      throw error;
    }
    if (status === 403 || status === 401) {
      throw new UnauthenticatedError(`error contacting api ${requestInfo}`);
    } else if (status === 409) {
      const responseBody = await response.json();
      const error = new FormSubmissionError(
        `error contacting api`,
        response,
        responseBody
      );
      // Attach the parsed response data to the error object for easy access
      (error as any).responseData = responseBody;
      throw error;
    } else if (status === 404) {
      throw new PageNotFoundError(`page not found`);
    }
  }
  // automatically reset token if a different one comes through
  const authHandler = getAuthHandler();
  const authorization = response.headers.get(`Authorization`);
  if (authorization && authHandler) {
    authHandler.token = authorization;
  }

  const location = response.headers.get(`location`);
  if (location && authHandler) {
    authHandler.redirectLocation = location;
  }

  return response;
};

const request = async (
  input: RequestInfo,
  init?: RequestInit
): Promise<Response> => {
  const token = getAuthHandler().token;
  return _execRequest(token, input, init);
};

/**
 * Sends a JSON request to the API and returns the parsed response.
 *
 * @param {RequestInfo} input - The request URL or a Request object.
 * @param {RequestInit} init - Optional request options.
 * @param {boolean} optRequireAuth - Whether the request requires authentication.
 * @returns A Promise that resolves to the parsed JSON response.
 * @throws {UnauthenticatedError} If the request requires authentication but no token is available.
 */
const requestJSON = async <T>(
  input: RequestInfo,
  init: RequestInit = {},
  optRequireAuth = false
): Promise<T> => {
  if (optRequireAuth && !getAuthHandler().token) {
    throw new UnauthenticatedError(`no token available`);
  }
  const response = await request(input, init);

  // no content responses result in an error when response.json() is invoked.
  if (response.status === 204) {
    return null;
  }

  return await response.json();
};

/**
 * Routes a CMx browser API call through client-api path resolution before
 * delegating to the browser `fetch`. Unlike `request`/`requestJSON`, this does
 * not attach the auth handler token; it is a thin path-resolving fetch wrapper.
 */
const fetchApi = async (
  input: RequestInfo,
  init?: RequestInit
): Promise<Response> => {
  return fetch(resolveRequestInfo(input), init);
};

export { request, requestJSON, fetchApi };
